import "./ShowAllPages.scss";

export interface ShowAllPagePreview {
  readonly url: string;
  readonly kind: "thumb" | "medium";
  readonly width: number;
  readonly height: number;
  readonly createdAt?: Date;
}

export interface ShowAllItem {
  readonly id: string;
  readonly name: string;
  readonly description?: string | null;
  readonly preview?: ShowAllPagePreview | null;
}

export interface ShowAllPagesConfig {
  readonly items?: readonly ShowAllItem[];
}

export interface ShowAllPagesAPI {
  readonly getElement: () => HTMLElement;
  readonly setPages: (items: readonly ShowAllItem[]) => void;
  readonly setVisible: (visible: boolean) => void;
  readonly destroy: () => void;
}

export const createShowAllPages = (
  config: ShowAllPagesConfig = {}
): ShowAllPagesAPI => {
  const container = document.createElement("div");
  container.className =
    "dedicated-placeholder dedicated-placeholder--show-all dedicated-placeholder--pages";

  container.innerHTML = `
    <div class="dedicated-placeholder__header">
      <h2 class="dedicated-placeholder__title">Pages</h2>
      <p class="dedicated-placeholder__subtitle">
        Quick overview of every page in the current project.
      </p>
    </div>
    <div class="dedicated-placeholder__body">
      <div class="dedicated-placeholder__grid" data-role="grid"></div>
      <div class="dedicated-placeholder__empty" data-role="empty">
        <p>No pages registered yet.</p>
      </div>
    </div>
  `;

  const gridEl = container.querySelector(
    '[data-role="grid"]'
  ) as HTMLElement;
  const emptyEl = container.querySelector(
    '[data-role="empty"]'
  ) as HTMLElement;

  let currentItems: readonly ShowAllItem[] = config.items ?? [];

  const renderItems = () => {
    gridEl.innerHTML = "";
    const items = currentItems ?? [];
    if (!items.length) {
      gridEl.style.display = "none";
      emptyEl.style.display = "flex";
      return;
    }

    gridEl.style.display = "grid";
    emptyEl.style.display = "none";

    for (const item of items) {
      const card = document.createElement("div");
      card.className = "dedicated-card";

      const preview = document.createElement("div");
      preview.className = "dedicated-card__preview";
      if (item.preview?.url) {
        preview.classList.add("dedicated-card__preview--image");
        const img = document.createElement("img");
        img.src = item.preview.url;
        img.alt = `${item.name} preview`;
        img.loading = "lazy";
        preview.appendChild(img);
      } else {
        preview.textContent = item.name.charAt(0).toUpperCase();
      }

      const name = document.createElement("span");
      name.className = "dedicated-card__name";
      name.textContent = item.name;

      card.appendChild(preview);
      card.appendChild(name);
      gridEl.appendChild(card);
    }
  };

  const setPages = (items: readonly ShowAllItem[]) => {
    currentItems = [...items];
    renderItems();
  };

  const setVisible = (visible: boolean) => {
    container.style.display = visible ? "flex" : "none";
  };

  const destroy = () => {
    container.remove();
  };

  renderItems();

  return {
    getElement: () => container,
    setPages,
    setVisible,
    destroy,
  };
};
